import { add, compareAsc } from 'date-fns';
import { ChangeEvent, useEffect, useState } from 'react';
import ReactDatePicker from 'react-datepicker';

import { CalendarIcon, LeftAngleIcon, RightAngleIcon } from '../../../../assets/svg';
import { CustomShareIcon } from '../../../../assets/svg/payment-links-custom/CustomShareIcon';
import { PlayIcon } from '../../../../assets/svg/payment-links-custom/PlayIcon';
import { Checkbox } from '../../../../components/form/checkbox';
import { Input } from '../../../../components/input';
import { ModalLinks } from '../../../../components/modal-links';
import { Spinner } from '../../../../components/spinner';
import { Switch } from '../../../../components/switch';
import { useFloatingAlert } from '../../../../hooks/useFloatingAlert';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Field, KeyTypes, TypesAllowed } from '../../../../models/payment-links-custom';
import { AmountType, Metadata, PaymentTypes } from '../../../../models/response-new-link';
import { PaymentLinksCustomServices } from '../../../../services/payment-links-custom';
import { getMonthAndYearFormat, HoursMinutes } from '../../../../utils/dates';
import { RangeDate } from '../../../payment-links-beta/components/new-single-payment';
import {
	CardContainer,
	CustomDataLink,
	DividerForm,
	HeaderContainer,
	ParagraphGlosa,
} from '../new-link/styles';
import { CircleStepOne, CircleStepTwo } from './components/icons';
import { PLCardLink } from './components/pl-card';
import { QRTabPL } from './components/qr-tab';
import {
	ChangeMonthContainer,
	CurrentMonthYear,
	DividerLink,
	HeaderMonthYearSelected,
	ShowDatePickerContainer,
} from './components/styles';
import {
	ActionsFooter,
	AlertButton,
	BodyStep,
	CardField,
	CardForm,
	ConfirmedButton,
	ContainerAmount,
	ContainerDates,
	ContainerForm,
	ContainerPaused,
	ContainerSelectNative,
	ContainerTextAndName,
	DataLink,
	Disclaimer,
	DividerTabs,
	HeaderStep,
	InputContainer,
	InputNative,
	ModalContainer,
	ModalFooter,
	PausedText,
	PaymentLinkButton,
	ReactiveButton,
	SelectNavite,
	ShareButton,
	TitleStep,
	WarningText,
} from './styles';
import { EditLinkProps } from './types';

const optionsMock = [
	{
		value: 'VARIABLE',
		label: 'VARIABLE',
		show: true,
	},
	{
		value: 'FIXED',
		label: 'FIJO',
		show: true,
	},
];

interface StatesPage {
	amount_type: AmountType;
	concat?: string;
	customer_email?: string;
	end_expired_at: Date | null;
	fields: Field[];
	has_end_expiration_date: boolean;
	init_expired_at: Date;
	is_rut_block?: boolean;
	merchant_code: string;
	metadata?: Metadata[];
	name: string;
	num_payments: number | null;
	order_amount_max: number | null;
	order_amount_min: number | null;
	order_amount: number | null;
	payment_cancellation_url?: string;
	payment_completed_url?: string;
	payment_type: PaymentTypes;
	loading: boolean;
}

const transformAmountStringtoNumber = (amount: string) => {
	if (!amount) return 0;
	else {
		const amountAux = amount.replace('$', '').replace(/,/g, '').replace(/\./g, '');
		return Number(amountAux);
	}
};

const formatNumber = (value: string) => {
	const parsedValue = Number(value.replace(/[^0-9]/g, ''));
	return parsedValue
		.toLocaleString('es-CL', {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		})
		.replace(',', '.');
};

const validateDateAndTime = ({ from, to }: RangeDate) => {
	if (!from || !to) return false;

	const rangeFromDate = new Date(from);
	const rangeToDate = new Date(to);

	return compareAsc(rangeFromDate, rangeToDate) <= 0;
};

const alphanumericWithSpacesRegex = /^[a-zA-Z0-9\s]*$/;
const currentValue = 'Ahora';

const EditLinkPL = ({ data, show, onClose, showPauseLinkModal }: EditLinkProps) => {
	const { merchantSelected } = useMerchant();

	const { handleShowAlert } = useFloatingAlert();

	const [showQR, setShowQR] = useState(false);

	const [showConfirmedModal, setShowConfirmedModal] = useState(false);
	const [resumeLink, setResumeLink] = useState(false);

	const handleShowConfirmedModal = () => setShowConfirmedModal(true);

	const [editing, setEditing] = useState(data.sub_status != 'paused');
	const [disabledSave, setDisabledSave] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);

	const [showCalendar, setShowCalendar] = useState({
		from: false,
		to: false,
	});

	const [withExpirationDate, setWithExpirationDate] = useState(data.endexpiredat ? true : false);

	const d = new Date();
	const [range, setRange] = useState<RangeDate>(() => ({
		from: data.initexpiredat ? new Date(data.initexpiredat) : d,
		to: data.endexpiredat != null ? new Date(data.endexpiredat) : d,
	}));

	const [orderHasError, setOrderHasError] = useState({
		min: false,
		max: false,
	});

	const [dateHasError, setDateHasError] = useState(false);
	const defaultValues: StatesPage = {
		has_end_expiration_date: data.endexpiredat ? true : false,
		amount_type: data.amounttype,
		end_expired_at: data.endexpiredat ? new Date(data.endexpiredat) : null,
		fields: data.fields,
		init_expired_at: data.initexpiredat ? new Date(data.initexpiredat) : range.from,
		loading: false,
		merchant_code: data.merchantcode,
		name: data.name,
		num_payments: data.numpayments,
		order_amount_max:
			data.amounttype === 'VARIABLE' && data.orderamountmax
				? transformAmountStringtoNumber(data.orderamountmax)
				: null,
		order_amount_min:
			data.amounttype === 'VARIABLE' && data.orderamountmin
				? transformAmountStringtoNumber(data.orderamountmin)
				: null,
		order_amount: data.amounttype === 'FIXED' ? transformAmountStringtoNumber(data.amount) : null,
		payment_type: data.paymenttype.toUpperCase() as PaymentTypes,
	};

	const [state, setState] = useState<StatesPage>(defaultValues);

	const [formattedAmount, setFormattedAmount] = useState(
		data.amounttype === 'FIXED' ? formatNumber(data.amount) : formatNumber(data.orderamountmin),
	);

	const [formattedMaxAmount, setFormattedMaxAmount] = useState(
		data.amounttype === 'VARIABLE' ? formatNumber(data.orderamountmax) : '0',
	);

	const handleToogleEditing = () => {
		setIsAnimating(true);
		setTimeout(() => {
			setEditing((prev) => !prev);
			setIsAnimating(false);
		}, 500);
	};

	const handleWithExpirationDate = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.checked) {
			setRange({
				from: state.init_expired_at,
				to: add(state.init_expired_at, { days: 1 }),
			});
			setState({
				...state,
				end_expired_at: range.to,
			});
		} else {
			setState({
				...state,
				end_expired_at: null,
			});

			setHoursMinutesStart([initValue, ...hoursMinutesStart]);
		}

		setWithExpirationDate(!withExpirationDate);
		setState({
			...state,
			has_end_expiration_date: !withExpirationDate,
		});
	};

	type RequiredFiles = {
		rut: boolean;
		names: boolean;
		email: boolean;
	};

	const [checkedRequiredFields, setCheckedRequiredFields] = useState<RequiredFiles>({
		rut: data.fields.find((f) => f.key === 'rut')?.required || false,
		names: data.fields.find((f) => f.key === 'names')?.required || false,
		email: data.fields.find((f) => f.key === 'rut')?.required || false,
	});

	const handleToggleCustomField = (key: KeyTypes) => {
		const existField = state.fields.find((f) => f.key === key);

		if (existField) {
			setState({
				...state,
				fields: state.fields.filter((f) => f.key !== key),
			});
			setCheckedRequiredFields((prev) => ({
				...prev,
				[key]: false,
			}));
		} else {
			let placeholder = '';
			if (key === 'rut') {
				placeholder = '00.000.000-X';
			} else if (key === 'email') {
				placeholder = 'maria.alvarado@gmail.com';
			} else {
				placeholder = 'Maria Alvarado';
			}
			setState({
				...state,
				fields: [
					...state.fields,
					{
						key,
						type: 'TEXT',
						type_var: key === 'email' ? 'email' : 'text',
						required: false,
						value: null,
						label:
							key === 'rut' ? 'Rut' : key === 'email' ? 'Correo electrónico' : 'Nombre y apellido',
						placeholder,
					},
				],
			});
			setCheckedRequiredFields((prev) => {
				return {
					...prev,
					[key]: false,
				};
			});
		}
	};

	const handleToggleRequired = (key: KeyTypes) => {
		if (checkedRequiredFields[key]) {
			//Set to false the require
			setState({
				...state,
				fields: state.fields.map((field) =>
					field.key === key ? { ...field, required: false } : field,
				),
			});
			setCheckedRequiredFields({
				...checkedRequiredFields,
				[key]: false,
			});
		} else {
			//Set to true the require
			setState({
				...state,
				fields: state.fields.map((field) =>
					field.key === key ? { ...field, required: true } : field,
				),
			});
			setCheckedRequiredFields({
				...checkedRequiredFields,
				[key]: true,
			});
		}
	};

	const handleCloseModal = () => {
		setState(defaultValues);
		if (showQR) {
			onClose(true);
		} else {
			onClose();
		}
	};

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		let dinamicValue: any = value;

		if (name === 'num_payments') {
			dinamicValue = Number(value);
		}
		if (state.amount_type === 'FIXED' && name === 'order_amount') {
			setState({
				...state,
				order_amount_min: null,
				order_amount_max: null,
			});
		}

		if (state.amount_type === 'VARIABLE' && name === 'order_amount') {
			setState({
				...state,
				order_amount: null,
			});
		}
		setState({ ...state, [name]: dinamicValue });
	};

	const onChangeDate = (selectedDate: Date) => {
		if (!selectedDate) return;

		setRange({
			...range,
			to: selectedDate,
		});
		setState((prev) => ({
			...prev,
			end_expired_at: selectedDate,
		}));
		setShowCalendar({ ...showCalendar, ['to']: false });
	};

	const handleKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!alphanumericWithSpacesRegex.test(e.key)) {
			e.preventDefault();
		}
	};

	const onChangeHours = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === '') return;

		const [hours, minutes] = e.target.value.split(':');
		//Set to the range
		const rangeTo = range.to;
		const parsedHours = hours[0] === '0' ? Number(hours[1]) : Number(hours);
		const parsedMinutes = minutes[0] === '0' ? Number(minutes[1]) : Number(minutes);
		rangeTo.setHours(parsedHours, parsedMinutes, 0);
		setRange({ ...range, to: rangeTo });
	};

	const onBlurHours = (e: ChangeEvent<HTMLInputElement>) => {
		//Have to verify if the value is valid, if not, set the default value
		if (e.target.value === '') return;

		const [hours, minutes] = e.target.value.split(':');
		const rangeTo = range.to;
		const parsedHours = hours[0] === '0' ? Number(hours[1]) : Number(hours);
		const parsedMinutes = minutes[0] === '0' ? Number(minutes[1]) : Number(minutes);
		rangeTo.setHours(parsedHours, parsedMinutes, 0);
		const dateRangeExample: RangeDate = {
			from: range.from,
			to: rangeTo,
		};
		if (range.from.getDate() === range.to.getDate()) {
			if (validateDateAndTime(dateRangeExample)) {
				setState({ ...state, end_expired_at: rangeTo });
				setDateHasError(false);
			} else {
				handleShowAlert({
					message: 'La fecha de vencimiento debe ser mayor a la fecha de inicio',
					type: 'error',
				});
				setDateHasError(true);
			}
		} else {
			setState({ ...state, end_expired_at: rangeTo });
			setDateHasError(false);
		}
	};

	const validateFields = (state: StatesPage) => {
		if (!state.name || !state.merchant_code) {
			setDisabledSave(true);
			return;
		}

		// Amount validation based on type
		if (state.amount_type === 'FIXED') {
			if (!state.order_amount) {
				setDisabledSave(true);
				return;
			}
		} else {
			if (!state.order_amount_min || !state.order_amount_max) {
				setDisabledSave(true);
				return;
			}
			if (state.order_amount_min > state.order_amount_max) {
				setDisabledSave(true);
				return;
			}
		}

		// Validate dates
		if (!state.init_expired_at) {
			setDisabledSave(true);
			return;
		}

		// If expiration date is set, validate it
		if (state.end_expired_at) {
			if (compareAsc(state.end_expired_at, state.init_expired_at) === -1) {
				setDisabledSave(true);
				return;
			}
		}

		// For recurrent payments, validate num_payments
		if (state.payment_type === 'RECURRENT' && state.num_payments === null) {
			setDisabledSave(true);
			return;
		}

		if (state.amount_type === 'VARIABLE' && state.order_amount_min && state.order_amount_max) {
			if (state.order_amount_min > state.order_amount_max) {
				setDisabledSave(true);
				return;
			}
		}
		setDisabledSave(false);
	};

	const renderAmount = () => {
		if (state.amount_type === 'VARIABLE') {
			return `${formatNumber(String(state.order_amount_min))} - $${formatNumber(
				String(state.order_amount_max),
			)}`;
		} else {
			return `${formatNumber(String(state.order_amount))}`;
		}
	};

	const initValue = {
		value: currentValue,
		label: 'Ahora',
		show: true,
	};
	const [hoursMinutesStart, setHoursMinutesStart] = useState([initValue, ...HoursMinutes]);

	const handleChangeAmount = (
		event: ChangeEvent<HTMLInputElement>,
		setFunction: (value: string) => void,
	) => {
		const rawValue = event.target.value.replace(/[^0-9]/g, '');
		setFunction(rawValue);
	};

	const handleBlurAmount = (event: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		const parsedValue = Number(value.replace(/[^0-9]/g, ''));
		if (state.amount_type === 'VARIABLE') {
			if (
				Number(formattedAmount.replace(/[^0-9]/g, '')) >
				Number(formattedMaxAmount.replace(/[^0-9]/g, ''))
			) {
				handleShowAlert({
					message: 'El monto mínimo no puede ser mayor al monto máximo.',
					type: 'error',
				});
				setOrderHasError({
					...orderHasError,
					min: true,
				});
			} else {
				setOrderHasError({
					...orderHasError,
					min: false,
				});
			}
		}

		setState({
			...state,
			[name]: parsedValue,
		});
	};

	const getFormattedTime = (): string => {
		const aux = range.to.toLocaleTimeString();
		const [hours, minutes] = aux.split(':');
		//Add a zeros at the beginning of the string if it is less than 10
		const formattedHours = hours.length < 2 ? `0${hours}` : hours;
		const formattedMinutes = minutes.length < 2 ? `0${minutes}` : minutes;
		return `${formattedHours}:${formattedMinutes}`;
	};

	const handleEditLink = async () => {
		setState({
			...state,
			loading: true,
		});
		try {
			const response = await PaymentLinksCustomServices.editLinkCustom({
				presession_token: data.token,
				merchant_code: merchantSelected?.code || '',
				payment_type: (String(state.payment_type).toUpperCase() as TypesAllowed) || undefined,
				amount_type: state.amount_type,
				name: state.name,
				order_amount: state.order_amount || undefined,
				order_amount_min: state.order_amount_min || undefined,
				order_amount_max: state.order_amount_max || undefined,
				num_payments: state.num_payments || 0,
				end_expired_at: state.end_expired_at?.toISOString() || undefined,
				has_end_expiration_date: state.has_end_expiration_date || false,
				fields: state.fields.map((e) => ({
					...e,
					type_var: String(e.type_var).toLowerCase(),
				})),
			});
			if (response.code === 'OK') {
				handleShowAlert({
					message: 'Enlace editado correctamente. Se reflejarán los cambios en 1 o 2 minutos.',
					type: 'success',
				});
				setShowQR(true);
			} else {
				handleShowAlert({
					message: 'Error al editar el enlace.',
					type: 'error',
				});
			}
		} catch (error) {
			handleShowAlert({
				message: 'Error al editar el enlace. ',
				type: 'error',
			});
		} finally {
			setState({
				...state,
				loading: false,
			});
		}
	};

	const handleEditAndResumeLink = async () => {
		setState({
			...state,
			loading: true,
		});
		try {
			const promiseResumeLink = PaymentLinksCustomServices.ResumePaymentLinkCustom({
				token: data.token,
				merchantCode: merchantSelected?.code || '',
			});
			const promiseEditLink = PaymentLinksCustomServices.editLinkCustom({
				presession_token: data.token,
				merchant_code: merchantSelected?.code || '',
				payment_type: (String(state.payment_type).toUpperCase() as TypesAllowed) || undefined,
				amount_type: state.amount_type,
				name: state.name,
				order_amount: state.order_amount || undefined,
				order_amount_min: state.order_amount_min || undefined,
				order_amount_max: state.order_amount_max || undefined,
				num_payments: state.num_payments || 0,
				end_expired_at: state.end_expired_at?.toISOString() || undefined,
				has_end_expiration_date: state.has_end_expiration_date || false,
				fields: state.fields.map((e) => ({
					...e,
					type_var: String(e.type_var).toLowerCase(),
				})),
			});

			const response = await Promise.all([promiseEditLink, promiseResumeLink]);
			if (response[0].code === 'OK' && response[1].code === 'OK') {
				handleShowAlert({
					message:
						'Enlace editado y reanudado correctamente. Se reflejarán los cambios en 1 o 2 minutos.',
					type: 'success',
				});
				onClose();
			} else {
				handleShowAlert({
					message: 'Error al editar y/o Reanudar el enlace. ',
					type: 'error',
				});
			}
		} catch (error) {
			handleShowAlert({
				message: 'Error al editar y/o Reanudar el enlace. ',
				type: 'error',
			});
		} finally {
			setState({
				...state,
				loading: false,
			});
			setShowConfirmedModal(false);
		}
	};

	useEffect(() => {
		validateFields(state);
	}, [state]);

	const renderButtonFooter = () => {
		if (data.sub_status === 'paused' && !editing && !showQR) {
			return (
				<>
					<ContainerPaused isAnimating={isAnimating}>
						<PausedText>Enlace pausado</PausedText>
						<ReactiveButton
							onClick={() => {
								handleToogleEditing();
								setResumeLink(true);
							}}
						>
							<PlayIcon />
							Reactivar recepción de pagos
						</ReactiveButton>
					</ContainerPaused>
				</>
			);
		} else if (showQR) {
			return <></>;
		} else {
			return (
				<>
					<AlertButton
						isAnimating={data.sub_status === 'paused'}
						onClick={() => showPauseLinkModal(data)}
					>
						Pausar o Finalizar
					</AlertButton>
					<ActionsFooter isAnimating={data.sub_status === 'paused'}>
						{data.status === 'active' && (
							<ShareButton onClick={() => setShowQR(true)}>
								<CustomShareIcon />
								Compartir
							</ShareButton>
						)}
						<PaymentLinkButton disabled={disabledSave} onClick={() => handleShowConfirmedModal()}>
							Guardar modificación
						</PaymentLinkButton>
					</ActionsFooter>
				</>
			);
		}
	};

	const renderContent = () => {
		if (state.loading) {
			return (
				<ModalLinks
					type="large"
					title="Procesando información"
					show={show}
					onClose={handleCloseModal}
					allowCloseModal={false}
				>
					<Spinner message="Editando enlace de cobro..." />
				</ModalLinks>
			);
		} else if (showQR) {
			return (
				<ModalLinks
					type="medium"
					title="Código QR"
					show={show}
					onClose={handleCloseModal}
					allowCloseModal
				>
					<>
						<ModalContainer>
							<QRTabPL
								id="qr-pl"
								amount={renderAmount() || ''}
								merchantName={merchantSelected?.name || ''}
								paymentLinkUrl={data.token_url || ''}
								paymentLinkUrlQR={data.token_qr || ''}
								paymentLinkName={data.name || ''}
							/>
						</ModalContainer>
					</>
				</ModalLinks>
			);
		} else if (showConfirmedModal) {
			return (
				<ModalLinks
					type="medium"
					title="¿Desea realizar estos cambios?"
					show={show}
					onClose={handleCloseModal}
					allowCloseModal
				>
					<>
						<ModalContainer>
							<TitleStep>
								¿Está seguro que desea realizar estos cambios al enlace de cobro?
							</TitleStep>
						</ModalContainer>
						<ModalFooter>
							<ConfirmedButton
								onClick={() => {
									resumeLink ? handleEditAndResumeLink() : handleEditLink();
								}}
							>
								Aceptar
							</ConfirmedButton>
							<AlertButton onClick={handleCloseModal}>Cancelar</AlertButton>
						</ModalFooter>
					</>
				</ModalLinks>
			);
		} else {
			return (
				<ModalLinks
					type="large"
					title="Editar enlace de cobro"
					show={show}
					onClose={handleCloseModal}
					allowCloseModal
				>
					<>
						<ModalContainer>
							<ContainerForm>
								<DataLink>
									<HeaderStep>
										<CircleStepOne /> <TitleStep>Selecciona el tipo de cobro</TitleStep>
									</HeaderStep>
									<BodyStep>
										<PLCardLink
											payType={'unique'}
											selected={state.payment_type === 'UNIQUE'}
											disabled={!editing}
											onClick={() => {
												if (data.paymenttype === 'RECURRENT') {
													handleShowAlert({
														type: 'error',
														message: 'No puedes cambiar el tipo de cobro de recurrente a único.',
													});
												} else {
													setState({
														...state,
														payment_type: 'UNIQUE',
														num_payments: 1,
													});
												}
											}}
										/>
										<PLCardLink
											payType={'recurrent'}
											selected={state.payment_type === 'RECURRENT'}
											disabled={!editing}
											onClick={() => {
												setState({
													...state,
													payment_type: 'RECURRENT',
												});
											}}
										/>
									</BodyStep>
									<DividerLink />
									<ContainerAmount>
										<ContainerSelectNative>
											<SelectNavite
												value={state.amount_type}
												onChange={(e) => {
													const type = e.target.value as AmountType;
													if (type === 'FIXED') {
														setState({
															...state,
															amount_type: type,
															order_amount: state.order_amount_min,
															order_amount_min: null,
															order_amount_max: null,
														});
														setFormattedMaxAmount('0');
													} else {
														setState({
															...state,
															amount_type: type,
															order_amount: null,
															order_amount_min: state.order_amount,
															order_amount_max: null,
														});
													}
												}}
												disabled={!editing}
											>
												{optionsMock.map((opt) => (
													<option key={`opt-${opt.label}`} value={opt.value}>
														{opt.label}
													</option>
												))}
											</SelectNavite>
										</ContainerSelectNative>

										<Input
											hasFocus
											hasError={orderHasError.min}
											errorMessage="Monto mínimo no puede ser mayor a monto máximo"
											fontSize="16px"
											height={48}
											label={`${state.amount_type === 'VARIABLE' ? 'Monto mínimo' : 'Monto'}`}
											labelFocus
											min={0}
											maxLength={10}
											name={state.amount_type === 'VARIABLE' ? 'order_amount_min' : 'order_amount'}
											value={formatNumber(formattedAmount)}
											placeholder="$5.000"
											prefix="$"
											type="text"
											variant="large"
											disabled={!editing}
											onChange={(e) => {
												handleChangeAmount(e, setFormattedAmount);
											}}
											onBlur={(e) => handleBlurAmount(e)}
										/>

										{state.amount_type === 'VARIABLE' && (
											<Input
												hasFocus
												hasError={orderHasError.max}
												height={48}
												aria-label="Monto máximo permitido"
												aria-describedby="Monto máximo permitido"
												label="Monto máximo"
												fontSize="16px"
												labelFocus
												min={0}
												maxLength={10}
												name="order_amount_max"
												value={formatNumber(formattedMaxAmount)}
												onChange={(e) => {
													handleChangeAmount(e, setFormattedMaxAmount);
												}}
												onKeyDown={(e) => handleKeydown(e)}
												onBlur={(e) => handleBlurAmount(e)}
												placeholder="$1.000.000"
												prefix="$"
												type="text"
												variant="large"
												disabled={!editing}
											/>
										)}
									</ContainerAmount>
									{state.payment_type === 'RECURRENT' && (
										<ContainerTextAndName>
											<WarningText>
												Los montos máximos a transferir están sujetos a las restricciones de cada
												banco.
											</WarningText>
											{data.numpayments === 0 ? (
												<Input
													hasFocus
													height={48}
													label="Cantidad de pagos aceptados"
													fontSize="16px"
													labelFocus
													placeholder="Sin limite"
													variant="large"
													disabled={true}
												/>
											) : (
												<Input
													hasFocus
													height={48}
													min={0}
													label="Cantidad de pagos aceptados"
													fontSize="16px"
													labelFocus
													name="num_payments"
													onChange={(e) => {
														if (Number(e.target.value) >= data.numpayments) {
															handleChangeInput(e);
														} else {
															handleShowAlert({
																type: 'error',
																message:
																	'La cantidad de pagos debe ser mayor o igual a la cantidad de pagos actuales.',
															});
														}
													}}
													placeholder="Sin limite"
													type="number"
													value={state.num_payments || 0}
													variant="large"
													disabled={!editing || data.numpayments === 0}
													onKeyDown={(e) => handleKeydown(e)}
												/>
											)}
										</ContainerTextAndName>
									)}
									<DividerLink />
									<ContainerTextAndName>
										<Input
											hasFocus
											height={48}
											label="Nombre del enlace de cobro"
											labelFocus
											fontSize="16px"
											name="name"
											onChange={(e) => handleChangeInput(e)}
											onKeyDown={(e) => handleKeydown(e)}
											placeholder="Ej. nombre del cliente, referencia del cobro."
											variant="large"
											value={state.name}
											disabled={!editing}
										/>
									</ContainerTextAndName>
									<DividerLink />
									<ContainerTextAndName>
										<Disclaimer>Personaliza la fecha del enlace de cobro</Disclaimer>
										<ContainerDates>
											<InputContainer>
												<Input
													hasFocus
													labelFocus
													height={48}
													fontSize="16px"
													label="Fecha de inicio"
													value={range.from.toLocaleDateString()}
													variant="large"
													rightIcon={<CalendarIcon />}
													disabled={true}
												/>
											</InputContainer>
											<ContainerSelectNative width="100%">
												<InputNative value={data.initexpiredattime} disabled={true} type="text" />
											</ContainerSelectNative>
										</ContainerDates>
										<Checkbox
											label="Con fecha de vencimiento"
											name="withExpirationDate"
											onChange={(e) => {
												handleWithExpirationDate(e);
											}}
											checked={withExpirationDate}
											disabled={!editing}
											fontSize="12px"
										/>
										{withExpirationDate && (
											<ContainerDates>
												<InputContainer>
													<Input
														hasFocus
														labelFocus
														height={48}
														fontSize="16px"
														label="Fecha de vencimiento"
														value={range.to.toLocaleDateString()}
														variant="large"
														rightIcon={<CalendarIcon />}
														disabled={!editing}
														rightIconClick={() => {
															setShowCalendar({ ...showCalendar, to: !showCalendar.to });
														}}
														onClick={() => {
															setShowCalendar({ ...showCalendar, to: !showCalendar.to });
														}}
														onChange={() =>
															setShowCalendar({ ...showCalendar, to: !showCalendar.to })
														}
													/>
													{showCalendar.to && (
														<ShowDatePickerContainer y="bottom" x="left">
															<ReactDatePicker
																inline
																locale="es"
																selected={range.to}
																minDate={new Date()}
																onChange={(date) => onChangeDate(date as Date)}
																renderCustomHeader={(params) => (
																	<>
																		<HeaderMonthYearSelected>
																			<ChangeMonthContainer
																				onClick={() => {
																					if (!params.prevMonthButtonDisabled)
																						params.decreaseMonth();
																				}}
																				allowClick={!params.prevMonthButtonDisabled}
																			>
																				<LeftAngleIcon />
																			</ChangeMonthContainer>

																			<CurrentMonthYear>
																				{getMonthAndYearFormat(params.date)}
																			</CurrentMonthYear>

																			<ChangeMonthContainer
																				onClick={() => {
																					if (!params.nextMonthButtonDisabled)
																						params.increaseMonth();
																				}}
																				allowClick={!params.nextMonthButtonDisabled}
																			>
																				<RightAngleIcon />
																			</ChangeMonthContainer>
																		</HeaderMonthYearSelected>
																	</>
																)}
															/>
														</ShowDatePickerContainer>
													)}
												</InputContainer>
												<ContainerSelectNative width="100%">
													<InputNative
														hasError={dateHasError}
														value={getFormattedTime()}
														onChange={(e) => onChangeHours(e)}
														onBlur={(e) => onBlurHours(e)}
														disabled={!editing}
														type="time"
													/>
												</ContainerSelectNative>
											</ContainerDates>
										)}
									</ContainerTextAndName>
								</DataLink>
								<DividerTabs />
								<CustomDataLink>
									<HeaderContainer>
										<HeaderStep>
											<CircleStepTwo />
											<TitleStep>Personaliza la información del cliente</TitleStep>
										</HeaderStep>
										<ParagraphGlosa>
											Estos son los datos que el destinatario del enlace deberá rellenar para
											acceder a método de pago.
										</ParagraphGlosa>
									</HeaderContainer>
									<CardForm>
										<CardContainer
											disabled={!editing || !state.fields?.some((element) => element.key === 'rut')}
										>
											<CardField>
												<span>RUT</span>
												<Switch
													id="rut"
													name="rut"
													onChange={() => handleToggleCustomField('rut')}
													checked={state.fields?.some((element) => element.key === 'rut')}
													disabled={!editing}
												/>
											</CardField>
											<Checkbox
												label="Obligatorio"
												name={'required-rut'}
												disabled={
													!editing || !state.fields?.some((element) => element.key === 'rut')
												}
												checked={checkedRequiredFields.rut}
												onChange={() => handleToggleRequired('rut')}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
										<DividerForm />
										<CardContainer
											disabled={
												!editing || !state.fields?.some((element) => element.key === 'names')
											}
										>
											<CardField>
												<span>Nombre y apellidos</span>
												<Switch
													id="names"
													name="names"
													checked={state.fields?.some((element) => element.key === 'names')}
													onChange={() => handleToggleCustomField('names')}
													disabled={!editing}
												/>
											</CardField>
											<Checkbox
												onChange={() => handleToggleRequired('names')}
												label="Obligatorio"
												disabled={
													!editing || !state.fields?.some((element) => element.key === 'names')
												}
												checked={checkedRequiredFields.names}
												name={'required-names'}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
										<DividerForm />
										<CardContainer
											disabled={
												!editing || !state.fields?.some((element) => element.key === 'email')
											}
										>
											<CardField>
												<span>Correo electrónico</span>
												<Switch
													id="email"
													name="email"
													onChange={() => handleToggleCustomField('email')}
													disabled={!editing}
													checked={state.fields?.some((element) => element.key === 'email')}
												/>
											</CardField>
											<Checkbox
												label="Obligatorio"
												name={'required-email'}
												disabled={
													!editing || !state.fields?.some((element) => element.key === 'email')
												}
												checked={checkedRequiredFields.email}
												onChange={() => handleToggleRequired('email')}
												gap="4px"
												fontSize="12px"
											/>
										</CardContainer>
									</CardForm>
								</CustomDataLink>
							</ContainerForm>
						</ModalContainer>
						<ModalFooter>{renderButtonFooter()}</ModalFooter>
					</>
				</ModalLinks>
			);
		}
	};
	return <>{renderContent()}</>;
};

export { EditLinkPL };
